import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, CreditCard, FileText,
  Banknote, PiggyBank, RefreshCw, Building2, AlertTriangle,
  CheckCircle2, XCircle, ChevronRight, Clock
} from 'lucide-react';
import { SleepoverFinanceHealthTile } from '@/components/sleepover/SleepoverFinanceHealthTile';
import { SleepoverWatchdogTimelineTile } from '@/components/sleepover/SleepoverWatchdogTimelineTile';

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default', link }) {
  const variants = {
    default: 'bg-card hover:border-primary/30',
    primary: 'bg-primary/5 border-primary/20 hover:border-primary/40',
    success: 'bg-green-500/5 border-green-500/20 hover:border-green-500/40',
    warning: 'bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40',
    danger: 'bg-red-500/5 border-red-500/20 hover:border-red-500/40',
  };

  const content = (
    <Card className={`${variants[variant]} transition-colors ${link ? 'cursor-pointer' : ''}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            {Icon && (
              <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
                variant === 'danger' ? 'bg-red-500/10' :
                variant === 'warning' ? 'bg-amber-500/10' :
                variant === 'success' ? 'bg-green-500/10' :
                'bg-primary/10'
              }`}>
                <Icon className={`h-5 w-5 ${
                  variant === 'danger' ? 'text-red-500' :
                  variant === 'warning' ? 'text-amber-500' :
                  variant === 'success' ? 'text-green-500' :
                  'text-primary'
                }`} />
              </div>
            )}
            {link && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

function DetailCard({ title, items }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-center py-1.5">
            <div className="flex items-center gap-2">
              {item.icon && <item.icon className={`h-4 w-4 ${item.iconColor || 'text-muted-foreground'}`} />}
              <span className="text-sm">{item.label}</span>
            </div>
            <span className={`font-medium ${item.valueColor || ''}`}>{item.value}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function FinanceDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const [kpiRes, branchRes] = await Promise.all([
        api.get('/kpi/finance', { params }),
        api.get('/org/branches'),
      ]);
      setKpis(kpiRes.data.data);
      setBranches(branchRes.data.data || []);
    } catch (err) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch]);

  const handleBranchChange = (value) => {
    setSelectedBranch(value === 'all' ? '' : value);
    if (value && value !== 'all') {
      setSearchParams({ branchId: value });
    } else {
      setSearchParams({});
    }
  };

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const details = kpis?.details || {};

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${(amount || 0).toLocaleString()}`;
  };

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Finance Dashboard</h1>
          <p className="text-muted-foreground">Financial metrics and analysis</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranch || 'all'} onValueChange={handleBranchChange}>
            <SelectTrigger className="w-[200px]" data-testid="branch-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {(details.fundingPlans?.lowFundingPlans > 0 || details.invoices?.overdue > 0) && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <div className="flex-1">
                <p className="font-medium">Financial Alerts</p>
                <p className="text-sm text-muted-foreground">
                  {details.fundingPlans?.lowFundingPlans > 0 && (
                    <span className="text-amber-600">{details.fundingPlans.lowFundingPlans} funding plans low on balance</span>
                  )}
                  {details.fundingPlans?.lowFundingPlans > 0 && details.invoices?.overdue > 0 && ' · '}
                  {details.invoices?.overdue > 0 && (
                    <span className="text-red-600">{details.invoices.overdue} overdue invoices</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary KPIs - Row 1: Funding & Claims */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Funding & Claims</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Funding Utilisation"
            value={`${kpis?.fundingUtilisation || 0}%`}
            subtitle={`${formatCurrency(details.fundingPlans?.totalUsed || 0)} of ${formatCurrency(details.fundingPlans?.totalBudget || 0)}`}
            icon={PiggyBank}
            variant={kpis?.fundingUtilisation > 90 ? 'danger' : kpis?.fundingUtilisation > 75 ? 'warning' : 'primary'}
          />
          <KpiCard
            title="Claims Submitted"
            value={kpis?.claimsSubmitted || 0}
            subtitle={formatCurrency(details.claimBatches?.submittedAmount || 0)}
            icon={FileText}
            variant="success"
            link="/finance/claims"
          />
          <KpiCard
            title="Claims Rejected"
            value={kpis?.claimsRejected || 0}
            subtitle={formatCurrency(details.claimBatches?.rejectedAmount || 0)}
            icon={XCircle}
            variant={kpis?.claimsRejected > 0 ? 'danger' : 'default'}
            link="/finance/claims"
          />
          <KpiCard
            title="Approved Not Invoiced"
            value={kpis?.approvedNotInvoiced || 0}
            subtitle={formatCurrency(details.approvedNotInvoiced?.amount || 0)}
            icon={Clock}
            variant={kpis?.approvedNotInvoiced > 10 ? 'warning' : 'default'}
            link="/finance/claims"
          />
        </div>
      </div>

      {/* Primary KPIs - Row 2: Revenue & Margin */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Revenue & Margin</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Payroll Cost"
            value={formatCurrency(kpis?.payrollCost || 0)}
            subtitle={`${details.payrollBatches?.hours || 0}h · ${details.payrollBatches?.staffPaid || 0} staff`}
            icon={Banknote}
            link="/finance/payroll"
          />
          <KpiCard
            title="Participant Revenue"
            value={formatCurrency(kpis?.participantRevenue || 0)}
            subtitle={`${details.revenue?.paidClaimsCount || 0} paid claims`}
            icon={DollarSign}
            variant="success"
          />
          <KpiCard
            title="Branch Margin"
            value={formatCurrency(kpis?.branchMargin || 0)}
            subtitle={`${details.margin?.percent || 0}% margin`}
            icon={kpis?.branchMargin >= 0 ? TrendingUp : TrendingDown}
            variant={kpis?.branchMargin >= 0 ? 'success' : 'danger'}
          />
        </div>
      </div>

      {/* Sleep-Over Finance Health (feature-flag tolerant — hidden when flag is off) */}
      <SleepoverFinanceHealthTile />

      {/* Sleep-Over 48 h Validation Watchdog timeline (last 8 ticks) */}
      <SleepoverWatchdogTimelineTile />

      {/* Funding Utilisation Bar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Funding Utilisation</CardTitle>
          <CardDescription>
            {formatCurrency(details.fundingPlans?.totalUsed || 0)} used of {formatCurrency(details.fundingPlans?.totalBudget || 0)} total budget
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Progress 
            value={kpis?.fundingUtilisation || 0} 
            className={`h-3 ${kpis?.fundingUtilisation > 90 ? 'bg-red-200' : kpis?.fundingUtilisation > 75 ? 'bg-amber-200' : ''}`}
          />
          <div className="flex justify-between mt-2 text-sm">
            <span className="text-muted-foreground">{kpis?.fundingUtilisation || 0}% utilised</span>
            <span className="text-muted-foreground">{formatCurrency(details.fundingPlans?.remaining || 0)} remaining</span>
          </div>
          <div className="flex items-center gap-4 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-primary" />
              <span>{details.fundingPlans?.activePlans || 0} active plans</span>
            </div>
            {details.fundingPlans?.lowFundingPlans > 0 && (
              <div className="flex items-center gap-2 text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                <span>{details.fundingPlans.lowFundingPlans} low balance</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Detail Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Claims Summary */}
        <DetailCard
          title="Claims Summary"
          items={[
            { label: 'Submitted', value: `${kpis?.claimsSubmitted || 0} (${formatCurrency(details.claimBatches?.submittedAmount || 0)})`, icon: CheckCircle2, iconColor: 'text-green-500' },
            { label: 'Rejected', value: `${kpis?.claimsRejected || 0} (${formatCurrency(details.claimBatches?.rejectedAmount || 0)})`, icon: XCircle, iconColor: 'text-red-500', valueColor: kpis?.claimsRejected > 0 ? 'text-red-600' : '' },
            { label: 'Success Rate', value: `${details.claimBatches?.successRate || 0}%` },
            { label: 'Draft', value: details.claimBatches?.byStatus?.DRAFT?.count || 0 },
            { label: 'Ready to Submit', value: details.claimBatches?.byStatus?.READY?.count || 0 },
            { label: 'Paid', value: `${details.claimBatches?.byStatus?.PAID?.count || 0} (${formatCurrency(details.claimBatches?.byStatus?.PAID?.amount || 0)})` },
          ]}
        />

        {/* Payroll Summary */}
        <DetailCard
          title="Payroll Summary"
          items={[
            { label: 'Total Cost', value: formatCurrency(kpis?.payrollCost || 0), icon: Banknote },
            { label: 'Total Hours', value: `${details.payrollBatches?.hours || 0}h` },
            { label: 'Staff Paid', value: details.payrollBatches?.staffPaid || 0, icon: CheckCircle2, iconColor: 'text-green-500' },
            { label: 'Avg Hourly Cost', value: formatCurrency(details.payrollBatches?.avgHourlyCost || 0) },
            { label: 'Processed Batches', value: details.payrollBatches?.batchCount || 0 },
            { label: 'Pending Batches', value: `${details.payrollBatches?.pendingCount || 0} (${formatCurrency(details.payrollBatches?.pendingAmount || 0)})`, valueColor: details.payrollBatches?.pendingCount > 0 ? 'text-amber-600' : '' },
          ]}
        />

        {/* Invoice Summary */}
        <DetailCard
          title="Invoice Summary"
          items={[
            ...Object.entries(details.invoices?.byStatus || {}).map(([status, data]) => ({
              label: status,
              value: `${data.count} (${formatCurrency(data.amount)})`,
              icon: status === 'PAID' ? CheckCircle2 : status === 'SENT' ? FileText : Clock,
              iconColor: status === 'PAID' ? 'text-green-500' : status === 'OVERDUE' ? 'text-red-500' : 'text-muted-foreground',
            })),
            { label: 'Overdue', value: details.invoices?.overdue || 0, icon: AlertTriangle, iconColor: 'text-red-500', valueColor: details.invoices?.overdue > 0 ? 'text-red-600 font-bold' : '' },
          ]}
        />

        {/* Margin Analysis */}
        <DetailCard
          title="Margin Analysis"
          items={[
            { label: 'Revenue', value: formatCurrency(kpis?.participantRevenue || 0), icon: DollarSign, iconColor: 'text-green-500' },
            { label: 'Payroll Cost', value: `-${formatCurrency(kpis?.payrollCost || 0)}`, icon: Banknote, iconColor: 'text-red-500' },
            { label: 'Gross Margin', value: formatCurrency(kpis?.branchMargin || 0), icon: kpis?.branchMargin >= 0 ? TrendingUp : TrendingDown, iconColor: kpis?.branchMargin >= 0 ? 'text-green-500' : 'text-red-500', valueColor: kpis?.branchMargin >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold' },
            { label: 'Margin %', value: `${details.margin?.percent || 0}%` },
          ]}
        />
      </div>

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" asChild>
          <Link to="/finance/claims">View Claims</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/finance/invoices">View Invoices</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/finance/payroll">View Payroll</Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/reports/staff-costs">Cost Reports</Link>
        </Button>
      </div>

      {/* Data Sources & Period */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Data Sources:</span> funding plans, claim batches, invoices, payroll batches
            </div>
            <div>
              Period: {kpis?.period?.dateFrom} to {kpis?.period?.dateTo} · 
              Generated: {kpis?.generatedAt ? new Date(kpis.generatedAt).toLocaleString() : 'N/A'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
